// "Start a new chat from here" (presentations): the start screen's pending
// seed. The page the person pressed Ask on writes it, the start screen draws
// it as a chip, and Send creates the chat, then asks the server to copy the
// presentation into it (POST /agent/session/:id/seed). A failed copy removes
// the just-made chat so History keeps no empty one (the alert-setup pattern).
//
// The seed lives in sessionStorage under `agent-seed:<customer>`: tab-scoped,
// survives the navigation and a reload, gone when the tab closes. Storage may
// throw (private windows, blocked site data), so every access is guarded.
import { useRef, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { DECK_DATASET_RE, DECK_ID_RE, cleanLabel, cleanQuestion, isCellIndex } from '../agent/presentationFrame'
import type { SeedSessionRequest, SeedSessionResponse, SeedSourceKind } from '../types/gotypes'

export interface PendingSeed {
  source: { kind: SeedSourceKind; id: string }
  /** Where the person asked; absent when the whole page is the source (a plain story page, or the toolbar's button). */
  at?: { slide: string; layer?: string }
  figure?: { dataset: string; row: number; col: number }
  /** The source's job: the new chat starts on it, and the server refuses another. */
  job: string
  /** For the chip only: the server derives the real title. */
  label: string
  /** The figure as the page showed it, for the chip's tooltip. */
  figureText?: string
  /** The slide's or layer's starting question: the start screen pre-fills the composer with it. Never sent to the seed route. */
  question?: string
  /**
   * The walkthrough whose step pressed Start, if one did. Its chip shows only
   * while that walkthrough runs, and the walkthrough's end (Finish, close, or
   * another walkthrough's link) clears it, so a demo leaves no seed behind.
   */
  walkthrough?: string
}

export function seedStorageKey(customer: string): string {
  return `agent-seed:${customer}`
}

const SOURCE_ID_RE = /^[A-Za-z0-9-]{1,64}$/ // the seed route's rule
const WALKTHROUGH_SLUG_RE = /^[a-z0-9-]{1,64}$/ // help/walkthroughs: the ?walkthrough= value

/** A stored seed, or null when it is missing or not well formed (it came through storage, so it is checked again). */
export function parsePendingSeed(raw: unknown): PendingSeed | null {
  if (!raw || typeof raw !== 'object') return null
  const s = raw as Record<string, unknown>
  const source = s.source as Record<string, unknown> | undefined
  if (!source || (source.kind !== 'artifact' && source.kind !== 'dashboard')) return null
  if (typeof source.id !== 'string' || !SOURCE_ID_RE.test(source.id)) return null
  const at = s.at as Record<string, unknown> | undefined
  if (at !== undefined) {
    if (!at || typeof at.slide !== 'string' || !DECK_ID_RE.test(at.slide)) return null
    if (at.layer !== undefined && (typeof at.layer !== 'string' || !DECK_ID_RE.test(at.layer))) return null
  }
  // A figure is always somewhere on a slide.
  if (s.figure !== undefined && !at) return null
  if (typeof s.job !== 'string') return null
  // A bad tag is refused, not dropped: dropped, a walkthrough's seed would outlive it.
  if (s.walkthrough !== undefined && (typeof s.walkthrough !== 'string' || !WALKTHROUGH_SLUG_RE.test(s.walkthrough))) return null
  let figure: PendingSeed['figure']
  if (s.figure !== undefined) {
    const f = s.figure as Record<string, unknown>
    if (!f || typeof f.dataset !== 'string' || !DECK_DATASET_RE.test(f.dataset)) return null
    if (!isCellIndex(f.row) || !isCellIndex(f.col)) return null
    figure = { dataset: f.dataset, row: f.row, col: f.col }
  }
  return {
    source: { kind: source.kind, id: source.id },
    ...(at ? { at: at.layer ? { slide: at.slide as string, layer: at.layer as string } : { slide: at.slide as string } } : {}),
    ...(figure ? { figure } : {}),
    job: s.job,
    label: cleanLabel(s.label) || 'Presentation',
    ...(typeof s.figureText === 'string' && s.figureText ? { figureText: cleanLabel(s.figureText) } : {}),
    ...(cleanQuestion(s.question) ? { question: cleanQuestion(s.question) } : {}),
    ...(typeof s.walkthrough === 'string' ? { walkthrough: s.walkthrough } : {}),
  }
}

export function readPendingSeed(customer: string): PendingSeed | null {
  if (!customer) return null
  try {
    const raw = sessionStorage.getItem(seedStorageKey(customer))
    return raw ? parsePendingSeed(JSON.parse(raw)) : null
  } catch {
    return null
  }
}

export function writePendingSeed(customer: string, seed: PendingSeed): void {
  if (!customer) return
  try {
    sessionStorage.setItem(seedStorageKey(customer), JSON.stringify(seed))
  } catch {
    // Storage unavailable: the start screen opens without the chip.
  }
}

export function clearPendingSeed(customer: string): void {
  if (!customer) return
  try {
    sessionStorage.removeItem(seedStorageKey(customer))
  } catch {
    // Storage unavailable: nothing was kept.
  }
}

/**
 * Removes every customer's seed that a walkthrough made. The walkthrough
 * calls it when it ends; a seed the person made themselves stays.
 */
export function clearWalkthroughSeeds(): void {
  try {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key?.startsWith('agent-seed:')) keys.push(key)
    }
    for (const key of keys) {
      let made: unknown
      try {
        made = (JSON.parse(sessionStorage.getItem(key) ?? 'null') as { walkthrough?: unknown } | null)?.walkthrough
      } catch {
        continue
      }
      if (made !== undefined) sessionStorage.removeItem(key)
    }
  } catch {
    // Storage unavailable: nothing was kept.
  }
}

/** The seed route's body for a pending seed. */
export function seedRequestBody(seed: PendingSeed): SeedSessionRequest {
  return { source: seed.source, ...(seed.at ? { at: seed.at } : {}), ...(seed.figure ? { figure: seed.figure } : {}) }
}

/** What a failed seed tells the person, by the route's status. The route's own words are kept where they say more. */
export function seedErrorMessage(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { error?: unknown } } }
  const status = e?.response?.status
  const said = typeof e?.response?.data?.error === 'string' ? e.response.data.error : ''
  switch (status) {
    case 401: return 'You are signed out. Sign in to Platinum and try again.'
    case 404: return 'This page could not be found, or you can’t open it.'
    case 409: return 'This chat has already started'
    case 413: return 'This page is too large to copy into a chat (over 64 MB).'
    case 503: return 'The agent is not available just now. Try again in a moment.'
    case 400: return said ? `This chat could not start from this page: ${said}` : 'This chat could not start from this page.'
    default: return 'This page could not be copied into the new chat. Try again.'
  }
}

export interface PresentationSeed {
  /** The pending seed for this customer, or null. */
  seed: PendingSeed | null
  /** The person removed the chip. */
  handleRemove: () => void
  /** Send succeeded: the seed is used up. */
  handleUsed: () => void
  /**
   * Copies the presentation into a just-made chat. On failure the chat is
   * deleted (best effort) and the error is thrown with the server's words.
   */
  handleSeedSession: (sessionId: string) => Promise<SeedSessionResponse>
}

/**
 * `runningWalkthrough` is the slug of the walkthrough on screen, if any. A
 * seed that a walkthrough made is not offered once that walkthrough is over.
 */
export default function usePresentationSeed(customer: string, runningWalkthrough?: string): PresentationSeed {
  const [stored, setSeed] = useState<PendingSeed | null>(() => readPendingSeed(customer))
  const seed = stored?.walkthrough && stored.walkthrough !== runningWalkthrough ? null : stored
  // A customer switch without a remount reads that customer's seed.
  // Render-phase guard: initialisation from a changed input, nothing to clean up.
  const customerRef = useRef(customer)
  if (customerRef.current !== customer) {
    customerRef.current = customer
    setSeed(readPendingSeed(customer))
  }

  const handleRemove = () => {
    clearPendingSeed(customer)
    setSeed(null)
  }

  const handleSeedSession = async (sessionId: string): Promise<SeedSessionResponse> => {
    if (!seed) throw new Error('Nothing to start this chat from')
    try {
      const resp = await axios.post<SeedSessionResponse>(
        `${API_BASE_URL}/agent/session/${encodeURIComponent(sessionId)}/seed`,
        seedRequestBody(seed),
      )
      return resp.data
    } catch (err) {
      // Not seeded: this chat would sit in History without the presentation,
      // and the next Send would make another. Remove it; the chip stays.
      await axios.delete(`${API_BASE_URL}/agent/session/${encodeURIComponent(sessionId)}`).catch(() => undefined)
      throw new Error(seedErrorMessage(err))
    }
  }

  return { seed, handleRemove, handleUsed: handleRemove, handleSeedSession }
}
